import React, { useEffect, useMemo, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import {
  CfrBody,
  LibraryFacilityType,
  LibraryPanel,
  LibrarySearchHit,
  RequirementCategory,
  RequirementItem,
  US_STATES,
  cfrSectionCount,
  facilityLabel,
  stateName,
  CfrPart,
} from "@data/regulationLibraryCatalog";
import { getRegulationLibrary, RegulationLibraryPayload } from "@services/regulationLibrary";
import { AccreditationsResponse, AccreditationProgramData, FacilityStateRequirement, OperationalDocument, RegulatoryRequirement } from "@services/facilityCompliance";
import AccreditationCalendarView, { AccreditationSectionsView } from "../AccreditationCalendarView";
import searchIcon from "@assets/regulation-library/search.svg";
import federalIcon from "@assets/regulation-library/federal.svg";
import cfrIcon from "@assets/regulation-library/cfr.svg";
import stateIcon from "@assets/regulation-library/state.svg";
import opsIcon from "@assets/regulation-library/ops.svg";
import checkIcon from "@assets/regulation-library/check.svg";
import documentCopyIcon from "@assets/regulation-library/document-copy.svg";
import chevronDownIcon from "@assets/regulation-library/chevron-down.svg";
import scalesIcon from "@assets/regulation-library/scales.svg";
import arrowUpRightIcon from "@assets/regulation-library/arrow-up-right.svg";
import arrowSquareUpRightIcon from "@assets/regulation-library/arrow-square-up-right.svg";
import stateChevronIcon from "@assets/regulation-library/state-chevron.svg";

const VISIBLE_SECTIONS = 4;

const TABS: { id: "federal" | "state" | "operational" | "accreditation"; label: string }[] = [
  { id: "federal", label: "Federal Requirements" },
  { id: "state", label: "State Requirements" },
  { id: "operational", label: "Operational Documents" },
  { id: "accreditation", label: "Accreditations" },
];

function Highlight({ text, query }: { text: string; query: string }) {
  const needle = query.trim();
  if (!needle) return <>{text}</>;
  const lower = text.toLowerCase();
  const q = needle.toLowerCase();
  const nodes: React.ReactNode[] = [];
  let cursor = 0;
  let index = lower.indexOf(q, cursor);
  let key = 0;
  while (index >= 0) {
    if (index > cursor) nodes.push(text.slice(cursor, index));
    nodes.push(
      <Box key={key} component="mark" sx={{ backgroundColor: "#d7f6fb", color: "inherit", p: 0 }}>
        {text.slice(index, index + q.length)}
      </Box>
    );
    key += 1;
    cursor = index + q.length;
    index = lower.indexOf(q, cursor);
  }
  if (cursor < text.length) nodes.push(text.slice(cursor));
  return <>{nodes}</>;
}

function activeTab(panel: LibraryPanel): (typeof TABS)[number]["id"] | null {
  if (panel.kind === "overview") return null;
  if (panel.kind === "state") return "state";
  if (panel.kind === "operational") return "operational";
  if (panel.kind === "accreditation") return "accreditation";
  return "federal";
}

const TOP_LEVEL_GROUPS = ["federal", "state", "operational", "accreditation"];
const FEDERAL_SUBGROUPS = ["regulatory", "cfr"];

function groupsFor(panel: LibraryPanel): string[] {
  if (panel.kind === "regulatory-overview" || panel.kind === "category") return ["federal", "regulatory"];
  if (panel.kind === "cfr-overview" || panel.kind === "cfr-body") return ["federal", "cfr"];
  if (panel.kind === "federal-overview") return ["federal"];
  if (panel.kind === "state") return ["state"];
  if (panel.kind === "operational") return ["operational"];
  if (panel.kind === "accreditation") return ["accreditation"];
  return [];
}

function toggleGroup(current: Record<string, boolean>, id: string): Record<string, boolean> {
  const opening = !current[id];
  if (!opening) return { ...current, [id]: false };
  if (TOP_LEVEL_GROUPS.includes(id)) return { [id]: true };
  if (FEDERAL_SUBGROUPS.includes(id)) {
    const next = { ...current, [id]: true };
    FEDERAL_SUBGROUPS.forEach((group) => {
      if (group !== id) next[group] = false;
    });
    return next;
  }
  return { ...current, [id]: true };
}

interface RegulationLibraryRequirementsProps {
  facility: LibraryFacilityType;
  stateCode: string;
  panel: LibraryPanel;
  bannerFacilityName?: string;
  showBanner: boolean;
  onStateChange: (code: string) => void;
  onPanelChange: (panel: LibraryPanel) => void;
  onBackToLanding: () => void;
  onDismissBanner: () => void;
}

export default function RegulationLibraryRequirements({
  facility,
  stateCode,
  panel,
  bannerFacilityName,
  showBanner,
  onStateChange,
  onPanelChange,
  onBackToLanding,
  onDismissBanner,
}: RegulationLibraryRequirementsProps) {
  const [query, setQuery] = useState("");
  const [openId, setOpenId] = useState<string | null>(null);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const [detail, setDetail] = useState<RegulationLibraryPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const label = detail?.facility ? facilityLabel(detail.facility) : facilityLabel(facility);
  const currentState = detail?.state?.name || stateName(stateCode);
  const bodies = detail?.cfrBodies ?? [];
  const categories = detail?.regulatoryCategories ?? [];
  const hits = detail?.searchHits ?? [];

  useEffect(() => {
    if (!stateCode) {
      setDetail(null);
      setError(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getRegulationLibrary({
      facilityType: facility.id,
      state: stateCode,
      q: query.trim() || undefined,
    })
      .then((payload) => {
        if (cancelled) return;
        setDetail(payload);
        setError(null);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setDetail(null);
          setError(err instanceof Error ? err.message : "Failed to load requirements");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [facility.id, stateCode, query]);
  useEffect(() => {
    const next: Record<string, boolean> = {};
    groupsFor(panel).forEach((id) => {
      next[id] = true;
    });
    setOpenGroups(next);
  }, [panel]);

  const grouped = useMemo(() => {
    const map = new Map<string, LibrarySearchHit[]>();
    hits.forEach((hit) => {
      const list = map.get(hit.group) ?? [];
      list.push(hit);
      map.set(hit.group, list);
    });
    return Array.from(map.entries());
  }, [hits]);

  const go = (next: LibraryPanel, itemId?: string) => {
    setOpenId(itemId ?? null);
    setQuery("");
    onPanelChange(next);
  };

  const crumbs = breadcrumb(label, panel, categories, bodies);

  if (!stateCode) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, width: "100%", pt: "24px", pb: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, border: "1px solid #d7f1f6", borderRadius: "8px", px: 2, py: 1 }}>
          <Typography sx={{ fontSize: 14, color: "#065b70", fontWeight: 600, minWidth: 48 }}>State</Typography>
          <Box
            component="select"
            aria-label="State"
            value=""
            onChange={(event) => onStateChange(event.target.value)}
            sx={{ flex: 1, height: 36, borderRadius: "8px", border: "1px solid #9ad7e4", backgroundColor: "#fff", px: 1, fontFamily: "Rethink Sans, sans-serif" }}
          >
            <option value="">Select State</option>
            {US_STATES.map((state) => (
              <option key={state.code} value={state.code}>{state.name}</option>
            ))}
          </Box>
        </Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center" }}>
          <Crumb label="Regulations Library" onClick={onBackToLanding} />
          <Typography sx={{ color: "#8aa0a8", fontSize: 13 }}>/</Typography>
          <Crumb label={label} active />
        </Box>
        <Typography sx={{ fontSize: 13, color: "#5c6b73" }}>{facility.name}</Typography>
        <Typography sx={{ fontFamily: "Rethink Sans, sans-serif", fontSize: 28, fontWeight: 500, color: "#12343d" }}>
          Facility Compliance Requirements
        </Typography>
        <Typography sx={{ fontSize: 16, color: "#3d4a50" }}>Select State to view facility compliance requirements</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, width: "100%", pt: "24px", pb: 4 }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center" }}>
        <Crumb label="Regulations Library" onClick={onBackToLanding} />
        {crumbs.map((crumb) => (
          <React.Fragment key={crumb.label}>
            <Typography sx={{ color: "#8aa0a8", fontSize: 13 }}>/</Typography>
            <Crumb label={crumb.label} onClick={crumb.panel ? () => go(crumb.panel as LibraryPanel) : undefined} active={!crumb.panel} />
          </React.Fragment>
        ))}
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "8px",
          flexWrap: "wrap",
          py: "8px",
          borderBottom: "1px solid #35d7e4",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0, flexWrap: "wrap" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Box component="img" src={arrowSquareUpRightIcon} alt="" width={24} height={24} />
            <Typography
              sx={{
                fontFamily: "Rethink Sans, sans-serif",
                fontWeight: 500,
                fontSize: 24,
                lineHeight: 1.5,
                letterSpacing: "-0.4px",
                color: "#05495a",
                whiteSpace: "nowrap",
              }}
            >
              Facility Compliance Requirements
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              px: "12px",
              py: "4px",
              borderRadius: "24px",
              backgroundColor: "#f3f9fa",
              boxShadow: "0px 4px 2px #e9f3f5",
              fontFamily: "Rethink Sans, sans-serif",
              fontWeight: 500,
              fontSize: 14,
              lineHeight: 1,
              letterSpacing: "-0.4px",
              color: "#033843",
              whiteSpace: "nowrap",
            }}
          >
            {label}
          </Box>
        </Box>
        <Box
          sx={{
            display: "none",
            alignItems: "center",
            gap: "2px",
            flexShrink: 0,
            width: { xs: "100%", md: 374 },
            maxWidth: "100%",
            boxSizing: "border-box",
            px: "24px",
            py: "6px",
            borderRadius: "12px",
            border: "1px solid #ccf5ff",
            backgroundColor: "#fcfcfc",
          }}
        >
          <Typography
            sx={{
              width: 60,
              flexShrink: 0,
              boxSizing: "border-box",
              px: "4px",
              py: "8px",
              fontFamily: "Rethink Sans, sans-serif",
              fontWeight: 500,
              fontSize: 16,
              lineHeight: "normal",
              color: "#065b70",
            }}
          >
            State
          </Typography>
          <Box sx={{ position: "relative", flex: 1, minWidth: 0 }}>
            <Box
              component="select"
              aria-label="Header state"
              disabled
              value={stateCode}
              onChange={(event) => onStateChange(event.target.value)}
              sx={{
                width: "100%",
                boxSizing: "border-box",
                appearance: "none",
                WebkitAppearance: "none",
                border: "1px solid #99d4e1",
                borderRadius: "8px",
                backgroundColor: "#fdffff",
                p: "8px",
                pr: "36px",
                fontFamily: "Rethink Sans, sans-serif",
                fontWeight: 400,
                fontSize: 14,
                lineHeight: "normal",
                color: "#262626",
              }}
            >
              {US_STATES.map((state) => (
                <option key={state.code} value={state.code}>
                  {state.name}
                </option>
              ))}
            </Box>
            <Box
              component="span"
              sx={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", display: "inline-flex", pointerEvents: "none" }}
            >
              <Box component="img" src={stateChevronIcon} alt="" width={20} height={20} />
            </Box>
          </Box>
        </Box>
      </Box>

      {error && <Typography sx={{ color: "#8a1f11", fontSize: 14 }}>{error}</Typography>}

      {showBanner && bannerFacilityName && (
        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, backgroundColor: "#e7f8fc", border: "1px solid #9ad7e4", borderRadius: "8px", px: 2, py: 1.25 }}>
          <Typography sx={{ fontSize: 14, color: "#065b70" }}>
            Showing regulations applicable to {bannerFacilityName}. Pre-filtered to your applicable sections.
          </Typography>
          <Box component="button" type="button" onClick={onDismissBanner} sx={{ border: 0, background: "transparent", color: "#0a829d", cursor: "pointer", fontWeight: 600 }}>
            Dismiss
          </Box>
        </Box>
      )}

      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, flexWrap: "wrap", alignItems: "center", py: "12px" }}>
        <Box sx={{ display: "flex", flexWrap: "wrap" }}>
          {TABS.map((tab) => {
            const selected = activeTab(panel) === tab.id && !query.trim();
            return (
              <Box
                key={tab.id}
                component="button"
                type="button"
                onClick={() => go(tabPanel(tab.id))}
                sx={{
                  border: 0,
                  borderBottom: selected ? "2px solid #0a829d" : "2px solid transparent",
                  background: "transparent",
                  color: selected ? "#0a829d" : "#5c6b73",
                  fontFamily: "Rethink Sans, sans-serif",
                  fontWeight: 600,
                  fontSize: 14,
                  px: 1.5,
                  py: 1,
                  cursor: "pointer",
                }}
              >
                {tab.label}
              </Box>
            );
          })}
        </Box>
        <Box sx={{ position: "relative", minWidth: 280, flex: "1 1 280px", maxWidth: 486 }}>
          <Box component="img" src={searchIcon} alt="" width={18} height={18} sx={{ position: "absolute", left: 14, top: 16 }} />
          <Box
            component="input"
            aria-label="Search across all regulations"
            value={query}
            placeholder="Search across all regulations..."
            onChange={(event) => setQuery(event.target.value)}
            sx={{
              width: "100%",
              boxSizing: "border-box",
              height: 50,
              borderRadius: "8px",
              border: "1px solid #9ad7e4",
              pl: "42px",
              pr: query ? "64px" : "12px",
              fontFamily: "Rethink Sans, sans-serif",
              fontSize: 14,
            }}
          />
          {query && (
            <Box
              component="button"
              type="button"
              onClick={() => setQuery("")}
              sx={{ position: "absolute", right: 10, top: 14, border: 0, background: "transparent", color: "#0a829d", cursor: "pointer", fontSize: 13 }}
            >
              Clear
            </Box>
          )}
        </Box>
      </Box>

      {query.trim() ? (
        <SearchResults
          query={query}
          total={hits.length}
          grouped={grouped}
          onOpen={(hit) => go(hit.panel)}
          onViewAll={(next) => go(next)}
        />
      ) : loading ? (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", minHeight: 360 }}>
          <CircularProgress size={28} sx={{ color: "#0a829d" }} aria-label="Loading requirements" />
        </Box>
      ) : (
        <Box sx={{ display: "flex", gap: 3, alignItems: "flex-start" }}>
          <RequirementsNav
            stateCode={stateCode}
            onStateChange={onStateChange}
            panel={panel}
            openId={openId}
            openGroups={openGroups}
            onToggleGroup={(id) => setOpenGroups((current) => toggleGroup(current, id))}
            categories={categories}
            bodies={bodies}
            stateItems={detail?.stateRequirements ?? []}
            operationalItems={detail?.operationalDocuments ?? []}
            accreditationItems={detail?.accreditations ?? []}
            onOpen={go}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <PanelBody
              panel={panel}
              facilityName={label}
              bodies={bodies}
              categories={categories}
              stateLabel={currentState}
              stateNote={detail?.stateNote ?? `State facility requirements and state-level regulatory items for ${currentState}.`}
              stateItems={detail?.stateRequirements ?? []}
              operationalItems={detail?.operationalDocuments ?? []}
              accreditationItems={detail?.accreditations ?? []}
              accreditationPrograms={detail?.accreditationPrograms}
              sourceRecords={detail?.sourceRecords}
              openId={openId}
              onToggle={(id) => setOpenId((current) => (current === id ? null : id))}
              onOpen={go}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
}

function tabPanel(id: (typeof TABS)[number]["id"]): LibraryPanel {
  if (id === "state") return { kind: "state" };
  if (id === "operational") return { kind: "operational" };
  if (id === "accreditation") return { kind: "accreditation" };
  return { kind: "federal-overview" };
}

function breadcrumb(
  label: string,
  panel: LibraryPanel,
  categories: RequirementCategory[],
  bodies: CfrBody[]
): { label: string; panel?: LibraryPanel }[] {
  const facilityCrumb = { label };
  if (panel.kind === "overview") return [facilityCrumb];
  if (panel.kind === "federal-overview") return [facilityCrumb, { label: "Federal Requirements" }];
  if (panel.kind === "regulatory-overview") {
    return [facilityCrumb, { label: "Federal Requirements", panel: { kind: "federal-overview" } }, { label: "Regulatory Requirements" }];
  }
  if (panel.kind === "category") {
    const category = categories.find((entry) => entry.id === panel.categoryId);
    return [
      facilityCrumb,
      { label: "Federal Requirements", panel: { kind: "federal-overview" } },
      { label: "Regulatory Requirements", panel: { kind: "regulatory-overview" } },
      { label: friendlyLabel(category?.title ?? "Category") },
    ];
  }
  if (panel.kind === "cfr-overview") {
    return [facilityCrumb, { label: "Federal Requirements", panel: { kind: "federal-overview" } }, { label: "Code of Federal Regulations" }];
  }
  if (panel.kind === "cfr-body") {
    const body = bodies.find((entry) => entry.id === panel.bodyId);
    return [
      facilityCrumb,
      { label: "Federal Requirements", panel: { kind: "federal-overview" } },
      { label: "Code of Federal Regulations", panel: { kind: "cfr-overview" } },
      { label: body?.name ?? "CFR" },
    ];
  }
  if (panel.kind === "state") return [facilityCrumb, { label: "State Requirements" }];
  if (panel.kind === "operational") return [facilityCrumb, { label: "Operational Documents" }];
  return [facilityCrumb, { label: "Accreditations" }];
}

function Crumb({ label, onClick, active }: { label: string; onClick?: () => void; active?: boolean }) {
  return (
    <Box
      component={onClick ? "button" : "span"}
      type={onClick ? "button" : undefined}
      onClick={onClick}
      sx={{
        border: 0,
        background: "transparent",
        p: 0,
        color: active ? "#065b70" : "#0a829d",
        fontWeight: active ? 600 : 500,
        fontSize: 13,
        fontFamily: "Rethink Sans, sans-serif",
        cursor: onClick ? "pointer" : "default",
      }}
    >
      {label}
    </Box>
  );
}

function NavRow({
  label,
  tone,
  expanded,
  hasChildren,
  onOpen,
  onToggle,
}: {
  label: string;
  depth: number;
  tone: "current" | "path" | "idle";
  expanded?: boolean;
  hasChildren?: boolean;
  onOpen: () => void;
  onToggle?: () => void;
}) {
  const text = friendlyLabel(label);
  const current = tone === "current";
  const onPath = tone !== "idle";
  return (
    <Box sx={{ display: "flex", alignItems: "center", borderRadius: "8px", backgroundColor: current ? "#e7f6fa" : "transparent" }}>
      <Box
        component="button"
        type="button"
        onClick={onOpen}
        sx={{
          flex: 1,
          minWidth: 0,
          textAlign: "left",
          border: 0,
          background: "transparent",
          color: onPath ? "#0a829d" : "#3d4a50",
          fontFamily: "Rethink Sans, sans-serif",
          fontWeight: onPath ? 600 : 500,
          fontSize: 14,
          lineHeight: "20px",
          cursor: "pointer",
          py: "7px",
          px: "10px",
          whiteSpace: "normal",
        }}
      >
        {text}
      </Box>
      {hasChildren ? (
        <Box
          component="button"
          type="button"
          aria-label={expanded ? `Collapse ${text}` : `Expand ${text}`}
          onClick={onToggle}
          sx={{ border: 0, background: "transparent", cursor: "pointer", display: "inline-flex", p: "4px", mr: "4px" }}
        >
          <Box component="img" src={chevronDownIcon} alt="" width={16} height={16} sx={{ transform: expanded ? "rotate(180deg)" : "none" }} />
        </Box>
      ) : null}
    </Box>
  );
}

function friendlyLabel(label: string): string {
  const raw = label.trim();
  if (!raw || /[A-Z\s&/]/.test(raw)) return raw;
  return raw.replace(/[_-]+/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function navTone(panel: LibraryPanel, openId: string | null, key: string): "current" | "path" | "idle" {
  if (key === "federal") {
    if (panel.kind === "federal-overview") return "current";
    if (panel.kind === "regulatory-overview" || panel.kind === "category" || panel.kind === "cfr-overview" || panel.kind === "cfr-body") return "path";
    return "idle";
  }
  if (key === "regulatory") {
    if (panel.kind === "regulatory-overview") return "current";
    if (panel.kind === "category") return "path";
    return "idle";
  }
  if (key === "cfr") {
    if (panel.kind === "cfr-overview") return "current";
    if (panel.kind === "cfr-body") return "path";
    return "idle";
  }
  if (key === "state") return panel.kind === "state" ? (openId ? "path" : "current") : "idle";
  if (key === "operational") return panel.kind === "operational" ? (openId ? "path" : "current") : "idle";
  if (key === "accreditation") return panel.kind === "accreditation" ? (openId ? "path" : "current") : "idle";
  if (panel.kind === "category" && panel.categoryId === key) return "current";
  if (panel.kind === "cfr-body" && panel.bodyId === key) return "current";
  if ((panel.kind === "state" || panel.kind === "operational" || panel.kind === "accreditation") && openId === key) return "current";
  return "idle";
}

function RequirementsNav({
  stateCode,
  onStateChange,
  panel,
  openId,
  openGroups,
  onToggleGroup,
  categories,
  bodies,
  stateItems,
  operationalItems,
  accreditationItems,
  onOpen,
}: {
  stateCode: string;
  onStateChange: (code: string) => void;
  panel: LibraryPanel;
  openId: string | null;
  openGroups: Record<string, boolean>;
  onToggleGroup: (id: string) => void;
  categories: RequirementCategory[];
  bodies: CfrBody[];
  stateItems: RequirementItem[];
  operationalItems: RequirementItem[];
  accreditationItems: RequirementItem[];
  onOpen: (panel: LibraryPanel, itemId?: string) => void;
}) {
  const tone = (key: string) => navTone(panel, openId, key);
  const childGroupSx = { ml: "14px", pl: "10px", display: "flex", flexDirection: "column", gap: "2px" } as const;
  return (
    <Box sx={{ width: 280, flexShrink: 0, display: "flex", flexDirection: "column", gap: 1 }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        <Typography sx={{ fontFamily: "Rethink Sans, sans-serif", fontSize: 14, fontWeight: 600, color: "#065b70" }}>State</Typography>
        <Box
          component="select"
          aria-label="State"
          value={stateCode}
          onChange={(event) => onStateChange(event.target.value)}
          sx={{ height: 36, borderRadius: "8px", border: "1px solid #99d4e1", backgroundColor: "#fdffff", px: 1, fontFamily: "Rethink Sans, sans-serif", fontSize: 14, color: "#262626" }}
        >
          {US_STATES.map((state) => (
            <option key={state.code} value={state.code}>{state.name}</option>
          ))}
        </Box>
      </Box>
      <NavRow label="Federal Requirements" depth={0} tone={tone("federal")} hasChildren expanded={!!openGroups.federal} onOpen={() => onOpen({ kind: "federal-overview" })} onToggle={() => onToggleGroup("federal")} />
      {openGroups.federal && (
        <Box sx={childGroupSx}>
          <NavRow label="Regulatory Requirements" depth={0} tone={tone("regulatory")} hasChildren expanded={!!openGroups.regulatory} onOpen={() => onOpen({ kind: "regulatory-overview" })} onToggle={() => onToggleGroup("regulatory")} />
          {openGroups.regulatory && (
            <Box sx={childGroupSx}>
              {categories.map((category) => (
                <NavRow key={category.id} label={category.title} depth={0} tone={tone(category.id)} onOpen={() => onOpen({ kind: "category", categoryId: category.id })} />
              ))}
            </Box>
          )}
          <NavRow label="Code of Federal Regulations" depth={0} tone={tone("cfr")} hasChildren expanded={!!openGroups.cfr} onOpen={() => onOpen({ kind: "cfr-overview" })} onToggle={() => onToggleGroup("cfr")} />
          {openGroups.cfr && (
            <Box sx={childGroupSx}>
              {bodies.map((body) => (
                <NavRow key={body.id} label={body.name} depth={0} tone={tone(body.id)} onOpen={() => onOpen({ kind: "cfr-body", bodyId: body.id })} />
              ))}
            </Box>
          )}
        </Box>
      )}
      <NavRow label="State Requirements" depth={0} tone={tone("state")} hasChildren expanded={!!openGroups.state} onOpen={() => onOpen({ kind: "state" })} onToggle={() => onToggleGroup("state")} />
      {openGroups.state && (
        <Box sx={childGroupSx}>
          {stateItems.map((item) => (
            <NavRow key={item.id} label={item.title} depth={0} tone={tone(item.id)} onOpen={() => onOpen({ kind: "state" }, item.id)} />
          ))}
        </Box>
      )}
      <NavRow label="Operational Documents" depth={0} tone={tone("operational")} hasChildren expanded={!!openGroups.operational} onOpen={() => onOpen({ kind: "operational" })} onToggle={() => onToggleGroup("operational")} />
      {openGroups.operational && (
        <Box sx={childGroupSx}>
          {operationalItems.map((item) => (
            <NavRow key={item.id} label={item.title} depth={0} tone={tone(item.id)} onOpen={() => onOpen({ kind: "operational" }, item.id)} />
          ))}
        </Box>
      )}
      <NavRow label="Accreditations" depth={0} tone={tone("accreditation")} hasChildren expanded={!!openGroups.accreditation} onOpen={() => onOpen({ kind: "accreditation" })} onToggle={() => onToggleGroup("accreditation")} />
      {openGroups.accreditation && (
        <Box sx={childGroupSx}>
          {accreditationItems.map((item) => (
            <NavRow key={item.id} label={item.title} depth={0} tone={tone(item.id)} onOpen={() => onOpen({ kind: "accreditation" }, item.id)} />
          ))}
        </Box>
      )}
    </Box>
  );
}

function CfrPartSections({ part, expanded, onToggle }: { part: CfrPart; expanded: boolean; onToggle: () => void }) {
  const [showAll, setShowAll] = useState(false);
  const hidden = Math.max(0, part.sections.length - VISIBLE_SECTIONS);
  const visible = showAll ? part.sections : part.sections.slice(0, VISIBLE_SECTIONS);
  return (
    <Box
      sx={{
        border: "1px solid #9ee5f4",
        borderRadius: "4px",
        backgroundColor: "#fff",
        boxShadow: "0px 4px 4px rgba(111, 211, 232, 0.15)",
      }}
    >
      <Box
        component="button"
        type="button"
        aria-expanded={expanded}
        onClick={onToggle}
        sx={{
          width: "100%",
          height: 37,
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center",
          gap: "8px",
          p: "8px",
          border: 0,
          background: "transparent",
          cursor: "pointer",
          textAlign: "left",
          fontFamily: "Rethink Sans, sans-serif",
        }}
      >
        <Box component="img" src={documentCopyIcon} alt="" width={20} height={20} />
        <Box component="span" sx={{ flexShrink: 0, width: 153, fontSize: 16, fontWeight: 500, color: "#0a829d", lineHeight: "normal", whiteSpace: "nowrap" }}>
          {part.citation}
        </Box>
        <Box component="span" sx={{ flex: 1, minWidth: 0, fontSize: 16, fontWeight: 500, color: "#262626", lineHeight: "normal" }}>
          {part.title}
        </Box>
        <Box component="span" sx={{ flexShrink: 0, fontSize: 16, fontWeight: 500, color: "#242424", lineHeight: "normal", whiteSpace: "nowrap" }}>
          {part.sections.length} sections
        </Box>
        <Box component="span" sx={{ display: "inline-flex", flexShrink: 0, transform: expanded ? "rotate(180deg)" : "none" }}>
          <Box component="img" src={chevronDownIcon} alt="" width={20} height={20} />
        </Box>
      </Box>
      {expanded && visible.map((cfr) => (
        <Box key={cfr.number} sx={{ px: 2, py: 1, display: "flex", justifyContent: "space-between", gap: 2, borderTop: "1px solid #eef8fb" }}>
          <Typography sx={{ fontSize: 14, color: "#12343d" }}>
            <Box component="span" sx={{ fontWeight: 600, color: "#065b70", mr: 1 }}>§ {cfr.number}</Box>
            {cfr.title}
          </Typography>
          <Box component="a" href={cfr.url} target="_blank" rel="noopener noreferrer" sx={{ color: "#0a829d", fontSize: 13, whiteSpace: "nowrap" }}>
            eCFR
          </Box>
        </Box>
      ))}
      {expanded && hidden > 0 && !showAll && (
        <Box sx={{ px: 2, py: 1, borderTop: "1px solid #eef8fb", fontSize: 13, color: "#5c6b73", display: "flex", justifyContent: "space-between", gap: 2 }}>
          <Box
            component="button"
            type="button"
            onClick={() => setShowAll(true)}
            sx={{ border: 0, background: "transparent", p: 0, cursor: "pointer", color: "#0a829d", fontWeight: 600, fontSize: 13, fontFamily: "Rethink Sans, sans-serif" }}
          >
            + {hidden} more sections
          </Box>
          <Box component="a" href={part.viewAllUrl} target="_blank" rel="noopener noreferrer" sx={{ color: "#0a829d", fontWeight: 600 }}>
            View all on eCFR
          </Box>
        </Box>
      )}
      {expanded && (
        <Box sx={{ px: 2, py: 1.25, borderTop: "1px solid #eef8fb" }}>
          <Box component="a" href={part.viewAllUrl} target="_blank" rel="noopener noreferrer" sx={{ color: "#0a829d", fontSize: 13, fontWeight: 600 }}>
            View all on eCFR
          </Box>
        </Box>
      )}
    </Box>
  );
}

function PanelBody({
  panel,
  facilityName,
  bodies,
  categories,
  stateLabel,
  stateNote,
  stateItems,
  operationalItems,
  accreditationItems,
  accreditationPrograms,
  sourceRecords,
  openId,
  onToggle,
  onOpen,
}: {
  panel: LibraryPanel;
  facilityName: string;
  bodies: CfrBody[];
  categories: RequirementCategory[];
  stateLabel: string;
  stateNote: string;
  stateItems: RequirementItem[];
  operationalItems: RequirementItem[];
  accreditationItems: RequirementItem[];
  accreditationPrograms?: AccreditationsResponse | null;
  sourceRecords?: RegulationLibraryPayload["sourceRecords"];
  openId: string | null;
  onToggle: (id: string) => void;
  onOpen: (panel: LibraryPanel) => void;
}) {
  if (panel.kind === "overview") {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography sx={{ fontFamily: "Rethink Sans, sans-serif", fontSize: 16, color: "#262626", lineHeight: 1.5 }}>
          Showing requirements for {facilityName} in {stateLabel}. Change state in the left panel if needed.
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" }, gap: "16px" }}>
          <OverviewCard icon={federalIcon} title="Federal Requirements" summary={`${categories.reduce((count, category) => count + category.items.length, 0)} federal requirements, plus CFR sections, for this facility.`} onClick={() => onOpen({ kind: "federal-overview" })} />
          <OverviewCard icon={stateIcon} title="State Requirements" summary={`${stateItems.length} state requirements for ${stateLabel}.`} onClick={() => onOpen({ kind: "state" })} />
          <OverviewCard icon={opsIcon} title="Operational Documents" summary={`${operationalItems.length} operational documents for this facility.`} onClick={() => onOpen({ kind: "operational" })} />
          <OverviewCard icon={checkIcon} title="Accreditations" summary={`${accreditationItems.length} accreditation programs for this facility.`} onClick={() => onOpen({ kind: "accreditation" })} />
        </Box>
      </Box>
    );
  }
  if (panel.kind === "federal-overview") {
    return (
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" }, gap: "16px" }}>
        <OverviewCard
          icon={federalIcon}
          title="Regulatory Requirements"
          summary={`${categories.reduce((count, category) => count + category.items.length, 0)} requirements for this facility and state.`}
          onClick={() => onOpen({ kind: "regulatory-overview" })}
        />
        <OverviewCard
          icon={cfrIcon}
          title="Code of Federal Regulations"
          summary={`${bodies.reduce((count, body) => count + cfrSectionCount(body), 0)} CFR sections for this facility.`}
          onClick={() => onOpen({ kind: "cfr-overview" })}
        />
      </Box>
    );
  }
  if (panel.kind === "regulatory-overview") {
    return (
      <Box>
        <SectionHeading title="Regulatory Requirements" note="" />
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" }, gap: 2 }}>
          {categories.map((category) => (
            <OverviewCard
              key={category.id}
              icon={federalIcon}
              title={friendlyLabel(category.title)}
              summary={`${category.items.length} items. ${category.items.map((entry) => entry.title).slice(0, 2).join(" · ")}`}
              onClick={() => onOpen({ kind: "category", categoryId: category.id })}
            />
          ))}
        </Box>
      </Box>
    );
  }
  if (panel.kind === "category") {
    const category = categories.find((entry) => entry.id === panel.categoryId);
    if (!category) return null;
    return (
      <Box>
        <SectionHeading title={friendlyLabel(category.title)} note={category.note || "Federal-level only. State-level items appear under State Requirements."} />
        <AccordionList items={category.items} sources={sourceRecords} sourceKind="regulatory" openId={openId} onToggle={onToggle} />
      </Box>
    );
  }
  if (panel.kind === "cfr-overview") {
    return (
      <Box>
        <SectionHeading title="Code of Federal Regulations" note="" />
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" }, gap: 2 }}>
          {bodies.map((body) => (
            <OverviewCard
              key={body.id}
              icon={cfrIcon}
              title={body.name}
              summary={`${cfrSectionCount(body)} sections. ${body.parts.map((part) => part.citation).join(", ")}`}
              onClick={() => onOpen({ kind: "cfr-body", bodyId: body.id })}
            />
          ))}
        </Box>
      </Box>
    );
  }
  if (panel.kind === "cfr-body") {
    const body = bodies.find((entry) => entry.id === panel.bodyId);
    if (!body) return null;
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: "24px" }}>
        <SectionHeading
          icon={scalesIcon}
          title={body.name}
          note={body.note || `${body.summary} — ${body.parts.length} CFR part${body.parts.length === 1 ? "" : "s"}.`}
        />
        <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {body.parts.map((part) => (
            <CfrPartSections key={part.id} part={part} expanded={openId === part.id} onToggle={() => onToggle(part.id)} />
          ))}
        </Box>
      </Box>
    );
  }
  if (panel.kind === "state") {
    return (
      <Box>
        <SectionHeading
          icon={scalesIcon}
          title="State Requirements"
          note={stateNote || `State facility requirements and state-level regulatory items for ${stateLabel}.`}
        />
        <AccordionList items={stateItems} sources={sourceRecords} sourceKind="state" openId={openId} onToggle={onToggle} />
      </Box>
    );
  }
  if (panel.kind === "operational") {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: "24px" }}>
        <SectionHeading icon={scalesIcon} title="Operational Documents" note="" />
        <AccordionList items={operationalItems} sources={sourceRecords} sourceKind="operational" openId={openId} onToggle={onToggle} />
      </Box>
    );
  }
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "24px" }}>
      <SectionHeading icon={scalesIcon} title="Accreditations" note="" />
      <AccordionList items={accreditationItems} programs={accreditationPrograms} openId={openId} onToggle={onToggle} />
    </Box>
  );
}

function SectionHeading({ title, note, icon }: { title: string; note: string; icon?: string }) {
  if (icon && !note) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          height: 37,
          boxSizing: "border-box",
          p: "8px",
        }}
      >
        <Box component="img" src={icon} alt="" width={20} height={20} />
        <Typography sx={{ fontFamily: "Rethink Sans, sans-serif", fontWeight: 700, fontSize: 20, color: "#065b70", lineHeight: "normal" }}>
          {title}
        </Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", p: icon ? "8px" : 0, mb: icon ? 0 : 1.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
        {icon ? <Box component="img" src={icon} alt="" width={20} height={20} /> : null}
        <Typography sx={{ fontFamily: "Rethink Sans, sans-serif", fontWeight: icon ? 700 : 600, fontSize: icon ? 20 : 18, color: "#065b70", lineHeight: "normal" }}>{title}</Typography>
      </Box>
      {note ? (
        <Typography sx={{ fontFamily: "Rethink Sans, sans-serif", fontSize: icon ? 14 : 13, lineHeight: icon ? "20px" : 1.4, color: icon ? "#065b70" : "#5c6b73" }}>
          {note}
        </Typography>
      ) : null}
    </Box>
  );
}

function OverviewCard({ icon, title, summary, onClick }: { icon: string; title: string; summary: string; onClick: () => void }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        textAlign: "left",
        border: "1px solid #065b70",
        borderRadius: "4px",
        backgroundColor: "#fff",
        p: 0,
        minHeight: 150,
        width: "100%",
        overflow: "hidden",
        cursor: "pointer",
        boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.05)",
        fontFamily: "Rethink Sans, sans-serif",
      }}
    >
      <Box sx={{ flex: 1, p: "8px", width: "100%", boxSizing: "border-box" }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: "8px", p: "4px" }}>
          <Box component="img" src={icon} alt="" width={20} height={20} />
          <Typography sx={{ flex: 1, fontWeight: 500, color: "#065b70", fontSize: 16, lineHeight: "normal" }}>{title}</Typography>
        </Box>
        <Typography sx={{ p: "4px", fontSize: 14, fontWeight: 400, color: "#4f4f4f", lineHeight: "normal" }}>{summary}</Typography>
      </Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end", width: "100%" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            p: "8px",
            borderLeft: "1px solid #086e86",
            borderTop: "1px solid #086e86",
            borderTopLeftRadius: "8px",
            backgroundColor: "#3095ab",
            boxShadow: "inset 1px 2px 4px #076074",
          }}
        >
          <Box component="img" src={arrowUpRightIcon} alt="" width={12} height={12} />
        </Box>
      </Box>
    </Box>
  );
}

function tableGrid(columns: string) {
  return { display: "grid", gridTemplateColumns: columns, gap: "8px", p: "12px 16px", alignItems: "start", minWidth: 880 } as const;
}

function DetailLink({ href, label }: { href: string; label: string }) {
  return (
    <Box
      component="a"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      sx={{ display: "inline-flex", border: "1px solid #0a829d", color: "#0a829d", borderRadius: "4px", px: "8px", py: "2px", fontSize: 12, fontWeight: 600, textDecoration: "none", fontFamily: "Rethink Sans, sans-serif" }}
    >
      {label}
    </Box>
  );
}

function PriorityLabel({ level }: { level: number }) {
  const label = level === 1 ? "High" : level === 2 ? "Medium" : "Low";
  const color = level === 1 ? { bg: "#fef2f2", text: "#991b1b", border: "#fecaca" } : level === 2 ? { bg: "#fffbeb", text: "#92400e", border: "#fed7aa" } : { bg: "#f0f9ff", text: "#1e40af", border: "#bfdbfe" };
  return <Box sx={{ display: "inline-flex", px: "8px", py: "2px", borderRadius: "12px", backgroundColor: color.bg, color: color.text, border: `1px solid ${color.border}`, fontSize: 12, fontWeight: 600 }}>{label}</Box>;
}

function RegulatoryDetail({ req }: { req: RegulatoryRequirement }) {
  const columns = "0.85fr 1.9fr 1fr 1.35fr 1fr 0.8fr";
  return (
    <Box sx={{ backgroundColor: "#fff", border: "1px solid #cfe6ed", borderRadius: "8px", overflow: "auto" }}>
      <Box sx={{ ...tableGrid(columns), backgroundColor: "#0a829d", color: "#fff", fontFamily: "Rethink Sans, sans-serif", fontSize: 14, fontWeight: 600 }}>
        <span>Priority</span><span>Description</span><span>Jurisdiction</span><span>Regulatory Body</span><span>Category</span><span>References</span>
      </Box>
      <Box sx={{ ...tableGrid(columns), fontFamily: "Rethink Sans, sans-serif", fontSize: 14, color: "#262626" }}>
        <PriorityLabel level={req.priority_level} />
        <span>{req.description}</span>
        <span>{req.jurisdiction_level || "Federal"}</span>
        <span>{req.regulatory_body || "—"}</span>
        <span>{req.category}</span>
        <span>{req.reference_url ? <DetailLink href={req.reference_url} label="Reference" /> : null}</span>
      </Box>
    </Box>
  );
}

function OperationalDetail({ doc }: { doc: OperationalDocument }) {
  const columns = "110px 130px 2.4fr 130px 120px 110px 100px";
  return (
    <Box sx={{ backgroundColor: "#fff", border: "1px solid #cfe6ed", borderRadius: "8px", overflow: "auto" }}>
      <Box sx={{ ...tableGrid(columns), backgroundColor: "#0a829d", color: "#fff", fontFamily: "Rethink Sans, sans-serif", fontSize: 14, fontWeight: 600 }}>
        <span>Document Code</span><span>Document Name</span><span>Description</span><span>Compliance Type</span><span>Jurisdiction Level</span><span>As of Date</span><span>References</span>
      </Box>
      <Box sx={{ ...tableGrid(columns), fontFamily: "Rethink Sans, sans-serif", fontSize: 14, color: "#262626" }}>
        <span>{doc.document_code}</span>
        <span>{doc.document_name}</span>
        <span>{doc.notes}</span>
        <span>{doc.compliance_type}</span>
        <span>{doc.jurisdiction_level}</span>
        <span>{doc.as_of_date}</span>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          {doc.source_url ? <DetailLink href={doc.source_url} label="Source" /> : null}
          {doc.document_url ? <DetailLink href={doc.document_url} label="Document" /> : null}
        </Box>
      </Box>
    </Box>
  );
}

function StateProgramDetail({ item }: { item: FacilityStateRequirement }) {
  const columns = "1fr 1.4fr 1fr 1fr 1.35fr 1.35fr 0.8fr";
  return (
    <Box sx={{ backgroundColor: "#fff", border: "1px solid #cfe6ed", borderRadius: "8px", overflow: "auto" }}>
      <Box sx={{ ...tableGrid(columns), backgroundColor: "#0a829d", color: "#fff", fontFamily: "Rethink Sans, sans-serif", fontSize: 14, fontWeight: 600 }}>
        <span>Facility Type</span><span>License/Program</span><span>Certificate of Need</span><span>Construction Review</span><span>Medicare Certification</span><span>Medicaid Enrollment</span><span>References</span>
      </Box>
      <Box sx={{ ...tableGrid(columns), fontFamily: "Rethink Sans, sans-serif", fontSize: 14, color: "#262626" }}>
        <span>{item.facility_type_name}</span>
        <span>{item.license_or_regulatory_program}</span>
        <span>{(item.certificate_of_need_required || "").toString().toUpperCase()}</span>
        <span>{(item.construction_review_required || "").toString().toUpperCase()}</span>
        <span>{item.medicare_certification_survey_required}</span>
        <span>{item.medicaid_enrollment_revalidation_required}</span>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          {item.legal_code_reference ? <DetailLink href={item.legal_code_reference} label="Reference" /> : null}
          {item.application_submission_url ? <DetailLink href={item.application_submission_url} label="Apply" /> : null}
        </Box>
      </Box>
    </Box>
  );
}

function accreditationProgramFor(
  programs: AccreditationsResponse | null | undefined,
  id: string,
): AccreditationProgramData | undefined {
  if (!programs) return undefined;
  const direct = programs[id];
  if (direct) return direct;
  return Object.values(programs).find((program) => program?.programSlug === id);
}

function AccordionList({
  items,
  openId,
  onToggle,
  programs,
  sources,
  sourceKind,
}: {
  items: RequirementItem[];
  openId: string | null;
  onToggle: (id: string) => void;
  programs?: AccreditationsResponse | null;
  sources?: RegulationLibraryPayload["sourceRecords"];
  sourceKind?: "regulatory" | "operational" | "state";
}) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      {items.map((entry) => {
        const open = openId === entry.id;
        return (
          <Box
            key={entry.id}
            sx={{
              border: "1px solid #9ee5f4",
              borderRadius: "4px",
              backgroundColor: "#fff",
              boxShadow: "0px 4px 4px rgba(111, 211, 232, 0.15)",
            }}
          >
            <Box
              component="button"
              type="button"
              aria-expanded={open}
              onClick={() => onToggle(entry.id)}
              sx={{
                width: "100%",
                minHeight: 37,
                height: "auto",
                boxSizing: "border-box",
                display: "flex",
                alignItems: "flex-start",
                gap: "8px",
                p: "8px",
                textAlign: "left",
                border: 0,
                background: "transparent",
                cursor: "pointer",
                fontFamily: "Rethink Sans, sans-serif",
              }}
            >
              <Box component="img" src={documentCopyIcon} alt="" width={20} height={20} sx={{ flexShrink: 0, mt: "1px" }} />
              <Box component="span" sx={{ flex: 1, minWidth: 0, fontSize: 16, fontWeight: 500, color: "#262626", lineHeight: "22px", whiteSpace: "normal" }}>
                {entry.title}
              </Box>
              {entry.aside ? (
                <Box component="span" sx={{ flexShrink: 0, alignSelf: "center", fontSize: 16, fontWeight: 500, color: "#242424", lineHeight: "22px", whiteSpace: "nowrap" }}>
                  {entry.aside}
                </Box>
              ) : null}
              <Box
                component="span"
                sx={{ display: "inline-flex", flexShrink: 0, alignSelf: "center", transform: open ? "rotate(180deg)" : "none" }}
              >
                <Box component="img" src={chevronDownIcon} alt="" width={20} height={20} />
              </Box>
            </Box>
            {open && (() => {
              const program = accreditationProgramFor(programs, entry.id);
              const hasProgramDetail = Boolean(program && ((program.calendarItems?.length ?? 0) > 0 || (program.sections?.length ?? 0) > 0));
              if (hasProgramDetail && program) {
                return (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2, p: "8px" }}>
                    {program.calendarItems && program.calendarItems.length > 0 ? (
                      <AccreditationCalendarView
                        calendarItems={program.calendarItems}
                        revision={program.revision}
                        effectiveDate={program.effectiveDate}
                        programLinks={program.programLinks}
                      />
                    ) : null}
                    {program.sections && program.sections.length > 0 ? (
                      <AccreditationSectionsView sections={program.sections} programLinks={program.programLinks} />
                    ) : null}
                  </Box>
                );
              }
              const regulatory = sourceKind === "regulatory" || sourceKind === "state" ? sources?.regulatory[entry.id] : undefined;
              const operational = sourceKind === "operational" ? sources?.operational[entry.id] : undefined;
              const stateProgram = sourceKind === "state" ? sources?.statePrograms[entry.id] : undefined;
              if (regulatory || operational || stateProgram) {
                return (
                  <Box sx={{ p: "12px" }}>
                    {stateProgram ? <StateProgramDetail item={stateProgram} /> : null}
                    {regulatory ? <RegulatoryDetail req={regulatory} /> : null}
                    {operational ? <OperationalDetail doc={operational} /> : null}
                  </Box>
                );
              }
              return (
              <Box sx={{ p: "8px" }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "16px",
                    p: "8px",
                    borderRadius: "4px",
                    border: "1px solid #e5f5f8",
                    backgroundColor: "#f3fdff",
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "Rethink Sans, sans-serif",
                      fontWeight: 400,
                      fontSize: 16,
                      lineHeight: "normal",
                      color: "#262626",
                    }}
                  >
                    {entry.description}
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: "5px" }}>
                    {entry.tags.map((tag) => (
                      <Box
                        key={tag}
                        sx={{
                          px: "8px",
                          py: "4px",
                          borderRadius: "4px",
                          backgroundColor: "#5bbfd6",
                          color: "#ffffff",
                          fontFamily: "Rethink Sans, sans-serif",
                          fontWeight: 500,
                          fontSize: 14,
                          lineHeight: "normal",
                        }}
                      >
                        {tag}
                      </Box>
                    ))}
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Typography
                      component="span"
                      sx={{
                        fontFamily: "Rethink Sans, sans-serif",
                        fontWeight: 700,
                        fontSize: 12,
                        lineHeight: "16px",
                        letterSpacing: "0.03px",
                        color: "#424242",
                      }}
                    >
                      Enforced by:&nbsp;
                    </Typography>
                    <Box
                      component="a"
                      href={entry.authorityUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        fontFamily: "Rethink Sans, sans-serif",
                        fontWeight: 400,
                        fontSize: 12,
                        lineHeight: "16px",
                        color: "#0a829d",
                        textDecoration: "none",
                      }}
                    >
                      {entry.authority}
                    </Box>
                  </Box>
                </Box>
              </Box>
              );
            })()}
          </Box>
        );
      })}
    </Box>
  );
}

function SearchResults({
  query,
  total,
  grouped,
  onOpen,
  onViewAll,
}: {
  query: string;
  total: number;
  grouped: [string, LibrarySearchHit[]][];
  onOpen: (hit: LibrarySearchHit) => void;
  onViewAll: (panel: LibraryPanel) => void;
}) {
  if (total === 0) {
    return <Typography sx={{ color: "#5c6b73" }}>No results for “{query}”.</Typography>;
  }
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography sx={{ fontSize: 14, color: "#065b70", fontWeight: 600 }}>{total} results</Typography>
      {grouped.map(([group, groupHits]) => (
        <Box key={group}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
            <Typography sx={{ fontWeight: 600, color: "#12343d", fontSize: 14 }}>{group}</Typography>
            <Box component="button" type="button" onClick={() => onViewAll(groupHits[0].panel)} sx={{ border: 0, background: "transparent", color: "#0a829d", cursor: "pointer", fontSize: 13 }}>
              View all
            </Box>
          </Box>
          {groupHits.map((hit) => (
            <Box
              key={hit.id}
              component="button"
              type="button"
              onClick={() => onOpen(hit)}
              sx={{ display: "block", width: "100%", textAlign: "left", border: 0, background: "transparent", cursor: "pointer", py: 0.75 }}
            >
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#065b70" }}>
                <Highlight text={hit.title} query={query} />
              </Typography>
              <Typography sx={{ fontSize: 13, color: "#3d4a50" }}>
                <Highlight text={hit.snippet} query={query} />
              </Typography>
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  );
}
